import { supabase } from '../supabase';
import { Recommendation, IRecommendationMonitor } from '../types';
import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Serviço para monitoramento e análise do sistema de recomendação
 * Inspirado nas melhores práticas do RecSysOps da Netflix
 */
export class RecommendationMonitor implements IRecommendationMonitor {
  private supabaseClient: SupabaseClient;
  private persistenceIntervalId: NodeJS.Timeout | null = null;
  private readonly persistenceIntervalMs: number;

  // KPIs do sistema de recomendação
  private metrics = {
    impressions: 0,
    clicks: 0,
    conversions: 0,
    lowScoreSelections: 0,
    anomalies: 0,
    coldStartImpressions: 0,
    recommendationsServed: 0,
    lastReset: new Date()
  };

  constructor(client: SupabaseClient = supabase, intervalMs?: number) {
    this.supabaseClient = client;
    this.persistenceIntervalMs = intervalMs ?? (3600 * 1000);

    this.resetMetrics();
    this.startPersistenceInterval();
  }

  private startPersistenceInterval(): void {
    if (this.persistenceIntervalId) {
        clearInterval(this.persistenceIntervalId);
        this.persistenceIntervalId = null;
    }
    
    if (this.persistenceIntervalMs > 0) {
        this.persistenceIntervalId = setInterval(() => {
            this.persistMetrics().catch(err => {
                console.error("Erro no intervalo de persistência de métricas:", err);
            });
        }, this.persistenceIntervalMs);

        console.log(`RecommendationMonitor: Persistência configurada a cada ${this.persistenceIntervalMs / 1000} segundos.`);
    } else {
        console.log('RecommendationMonitor: Persistência por intervalo desativada (intervalo <= 0).');
    }
  }

  public shutdown(persistLast: boolean = true): void {
    if (this.persistenceIntervalId) {
      clearInterval(this.persistenceIntervalId);
      this.persistenceIntervalId = null;
      console.log('RecommendationMonitor: Intervalo de persistência parado.');
      if (persistLast) {
          console.log('RecommendationMonitor: Persistindo métricas finais no shutdown...');
          this.persistMetrics().catch(err => console.error("Erro ao persistir métricas no shutdown:", err));
      }
    }
  }

  /**
   * Registra impressão de recomendações (quando são mostradas ao usuário)
   */
  public async trackImpressions(recommendations: Recommendation[]): Promise<void> {
    if (!recommendations || recommendations.length === 0) return;
    
    try {
      this.metrics.impressions += recommendations.length;
      this.metrics.recommendationsServed += recommendations.length;
      
      // Verificar recomendações de cold start (novos itens)
      const coldStartCount = recommendations.filter(rec => 
        rec.reason && rec.reason.includes('novo') || (rec.score != null && rec.score < 0.4)
      ).length;
      
      this.metrics.coldStartImpressions += coldStartCount;
      
    } catch (error) {
      console.error('Erro ao registrar impressões:', error);
    }
  }

  /**
   * Registra quando usuário clica em uma recomendação
   */
  public async trackClick(recommendation: Recommendation): Promise<void> {
    try {
      this.metrics.clicks++;
      
      // Verificar se o usuário clicou em uma recomendação com score baixo
      if (recommendation.score != null && recommendation.score < 0.5) {
        this.metrics.lowScoreSelections++;
      }
      
    } catch (error) {
      console.error('Erro ao registrar clique:', error);
    }
  }

  /**
   * Registra quando usuário realiza uma conversão (ex: compra, adiciona à coleção)
   */
  public async trackConversion(recommendation: Recommendation): Promise<void> {
    try {
      this.metrics.conversions++;
    } catch (error) {
      console.error('Erro ao registrar conversão:', error);
    }
  }

  /**
   * Calcula taxas de conversão e engagement
   */
  public getMetrics(): any {
    // Calcular métricas derivadas
    const clickThroughRate = this.metrics.impressions > 0 ? this.metrics.clicks / this.metrics.impressions : 0;
    const conversionRate = this.metrics.clicks > 0 ? this.metrics.conversions / this.metrics.clicks : 0;
    const anomalyRate = this.metrics.recommendationsServed > 0 ? this.metrics.anomalies / this.metrics.recommendationsServed : 0;
    const coldStartRate = this.metrics.impressions > 0 ? this.metrics.coldStartImpressions / this.metrics.impressions : 0;
    
    return {
      ...this.metrics,
      clickThroughRate,
      conversionRate,
      anomalyRate,
      coldStartRate,
      healthScore: this.calculateHealthScore()
    };
  }

  /**
   * Detecta anomalias no sistema de recomendação
   */
  public detectAnomalies(recommendations: Recommendation[]): string[] {
    const anomalies: string[] = [];
    
    // 1. Verificar se há muitas recomendações com score baixo
    const lowScoreCount = recommendations.filter(rec => rec.score != null && rec.score < 0.3).length;
    if (lowScoreCount > recommendations.length * 0.5) {
      anomalies.push('Alta proporção de recomendações com score baixo');
      this.metrics.anomalies++;
    }
    
    // 2. Verificar se há pouca diversidade nos algoritmos
    const algorithms = new Set(recommendations.map(rec => rec.algorithm).filter(Boolean));
    if (algorithms.size < 2 && recommendations.length > 5) {
      anomalies.push('Baixa diversidade de algoritmos');
      this.metrics.anomalies++;
    }
    
    // 3. Verificar se recomendações são muito similares entre si
    const uniqueCategories = new Set<string>();
    recommendations.forEach(rec => {
      if (rec.game && Array.isArray(rec.game.categories)) {
        rec.game.categories.forEach(cat => {
          if (typeof cat === 'string') {
             uniqueCategories.add(cat)
          }
        });
      }
    });
    
    if (uniqueCategories.size < 3 && recommendations.length > 5) {
      anomalies.push('Baixa diversidade de categorias');
      this.metrics.anomalies++;
    }
    
    return anomalies;
  }

  /**
   * Calcula score de saúde do sistema de recomendação
   */
  private calculateHealthScore(): number {
    // Score de 0 a 100 baseado em métricas
    const metricsSnapshot = this.getMetrics();
    const clickThroughRate = metricsSnapshot.clickThroughRate;
    const conversionRate = metricsSnapshot.conversionRate;
    const anomalyRate = metricsSnapshot.anomalyRate;
    
    // Fatores positivos
    let score = 50; // Base score
    
    // Aumenta se tiver alta taxa de cliques
    if (clickThroughRate > 0.05) score += 10;
    if (clickThroughRate > 0.1) score += 15;
    
    // Aumenta se tiver alta taxa de conversão
    if (conversionRate > 0.02) score += 10;
    if (conversionRate > 0.05) score += 15;
    
    // Fatores negativos
    // Diminui se tiver muitas anomalias
    if (anomalyRate > 0.05) score -= 15;
    if (anomalyRate > 0.1) score -= 15;
    
    // Diminui se muitas recomendações de baixo score forem selecionadas
    const lowScoreSelectionRate = this.metrics.clicks > 0 ? this.metrics.lowScoreSelections / this.metrics.clicks : 0;
    if (lowScoreSelectionRate > 0.3) score -= 10;
    if (lowScoreSelectionRate > 0.5) score -= 10;
    
    // Garantir que o score esteja entre 0 e 100
    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Reseta as métricas do monitor
   */
  public resetMetrics(): void {
    console.log('RecommendationMonitor: Resetando métricas...');
    this.metrics = {
      impressions: 0,
      clicks: 0,
      conversions: 0,
      lowScoreSelections: 0,
      anomalies: 0,
      coldStartImpressions: 0,
      recommendationsServed: 0,
      lastReset: new Date()
    };
  }

  /**
   * Persiste o snapshot atual das métricas no banco de dados (tabela system_stats).
   * Assume que a tabela system_stats foi adaptada para receber inserções históricas
   * com colunas correspondentes às métricas do monitor (ex: impressions, clicks, health_score, last_reset_at, recorded_at).
   */
  private async persistMetrics(): Promise<void> {
     // Cria um snapshot das métricas atuais para persistir
     const metricsToPersist = {
        impressions: this.metrics.impressions,
        clicks: this.metrics.clicks,
        conversions: this.metrics.conversions,
        low_score_selections: this.metrics.lowScoreSelections,
        anomalies: this.metrics.anomalies,
        cold_start_impressions: this.metrics.coldStartImpressions,
        recommendations_served: this.metrics.recommendationsServed,
        health_score: this.calculateHealthScore(),
        last_reset_at: this.metrics.lastReset.toISOString()
     };

    console.log('RecommendationMonitor: Tentando persistir métricas:', metricsToPersist);

    try {
        // Verifica se a tabela system_stats existe antes de tentar inserir
        const { data: tableExists, error: checkError } = await this.supabaseClient
            .from('system_stats')
            .select('id')
            .limit(1);
            
        if (checkError) {
            console.warn(
                'RecommendationMonitor: Falha ao verificar tabela system_stats.',
                `Mensagem: ${checkError.message}`,
                'As métricas serão registradas apenas em memória.'
            );
            return;
        }
            
        const { error } = await this.supabaseClient
            .from('system_stats')
            .insert(metricsToPersist);

        if (error) {
            console.error(
                'RecommendationMonitor: Falha ao persistir métricas do sistema.',
                `Mensagem: ${error.message}`,
                `Detalhes: ${error.details}`,
                `Hint: ${error.hint}`,
                `Code: ${error.code}`
            );
            
            // Tenta uma abordagem diferente se a primeira falhar - pode ser um problema de schema
            if (error.code === '42P01' || error.message.includes('does not exist')) {
                console.log('RecommendationMonitor: A tabela system_stats parece não existir. Métricas serão registradas apenas em memória.');
            } else if (error.code === '42703' || error.message.includes('column') || error.message.includes('does not exist')) {
                // Tenta inserir apenas colunas básicas que provavelmente existem no schema
                const simplifiedMetrics = {
                    impressions: this.metrics.impressions,
                    clicks: this.metrics.clicks,
                    conversions: this.metrics.conversions
                };
                
                const { error: simplifiedError } = await this.supabaseClient
                    .from('system_stats')
                    .insert(simplifiedMetrics);
                    
                if (simplifiedError) {
                    console.error('RecommendationMonitor: Também falhou com métricas simplificadas:', simplifiedError.message);
                } else {
                    console.log('RecommendationMonitor: Persistiu métricas simplificadas com sucesso.');
                }
            }
        } else {
            console.log('RecommendationMonitor: Métricas do sistema persistidas com sucesso.');
        }
    } catch (error: any) {
        console.error('RecommendationMonitor: Erro inesperado ao persistir métricas:', error.message || error);
    }
  }
} 